import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';

import { ENDPOINT } from '~/constants/endpoints';
import {
  PAGINATION_PAGE_SIZE_DEFAULT,
  type PaginationSortDirection,
} from '~/constants/pagination';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import {
  createInfiniteQuery,
  type InfiniteQueryOptionsType,
} from '~/utils/createInfiniteQuery';

import { queryKeysUser } from './queryKeys';

export type UsersQueryParams = {
  limit: number;
  offset: number;
  searchString: string;
  filterActive: boolean;
  filterCompany: string;
  filterNotParentOu?: UUID;
  filterParentOu: UUID;
  orderBy:
    | 'company_id'
    | 'company_name'
    | 'email'
    | 'first_name'
    | 'id'
    | 'is_active'
    | 'last_name';
  returnTotalCount: boolean;
  sort: PaginationSortDirection;
};

const defaultQueryParams: UsersQueryParams = {
  filterActive: undefined,
  filterCompany: undefined,
  filterNotParentOu: undefined,
  filterParentOu: undefined,
  limit: PAGINATION_PAGE_SIZE_DEFAULT,
  offset: 0,
  orderBy: 'email',
  returnTotalCount: true,
  searchString: undefined,
  sort: 'ASC',
};

export type UserListItem = {
  companyId: UUID;
  companyName?: string;
  email: string;
  firstName: string;
  id: UUID;
  isActive: boolean;
  isSynced: boolean;
  lastName: string;
  position: string;
  lastActiveDatetime: string;
};

export type UsersResponse = {
  data: readonly UserListItem[];
  hasNextPage: boolean;
  paginatedCount: number;
  totalCount: number;
};

/**
 * Generates the query options for the users query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param {Object} queryParams - The query parameters for fetching users.
 * @param {Object} options - Additional options for the query.
 * @returns {Object} The query options including queryKey, queryFn, and other settings.
 */
export const getUsersQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<UsersQueryParams>;
  options?: Omit<UseQueryOptions<UsersResponse>, 'queryKey' | 'queryFn'>;
}) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  qp.sort = qp.sort?.toUpperCase();

  return {
    queryFn: async () => fetchUsers(qp),
    queryKey: queryKeysUser.getAll(qp),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  };
};

/**
 * Fetches all users from the API.
 * @param {Object} queryParams - The query parameters for fetching users.
 * @returns {Promise<Object|null>} The user data if successful, null otherwise.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/get_users_user_get
 */
export const fetchUsers = async (queryParams: Partial<UsersQueryParams>) => {
  const mergedParams = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const qp = withoutObjectKeysWhereValueIs(
    {
      ...mergedParams,
      orderBy: mergedParams.orderBy
        ? toSnakeCase(mergedParams.orderBy)
        : undefined,
      sort: mergedParams.sort ? mergedParams.sort.toUpperCase() : undefined,
    },
    [undefined],
  );

  const response = await vestigasApi
    .get(ENDPOINT.USER.GET_ALL(), {
      searchParams: qp,
    })
    .json<UsersResponse>();

  return (
    response ?? {
      data: [],
      hasNextPage: false,
      paginatedCount: 0,
      totalCount: 0,
    }
  );
};

/**
 * React Query based custom hook for getting the data for all users with given query parameters.
 * Handles pagination and prefetches the next page of users for better performance.
 *
 * @param {Object} queryParams - The query parameters for fetching users.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult} The result of the useQuery hook.
 */
export const useQueryUsers = (
  queryParams: Partial<UsersQueryParams>,
  options: Parameters<typeof useQuery>[0],
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (queryParams?.searchString?.length > 0) {
      // Only prefetch if the user is not searching, to prevent firing lots of requests for costly queries.
      return;
    }

    queryClient.prefetchQuery(
      getUsersQueryOptions({
        options,
        queryParams: {
          ...queryParams,
          offset:
            (queryParams.offset ?? 0) +
            (queryParams.limit ?? defaultQueryParams.limit),
        },
      }),
    );
  }, [queryParams, options, queryClient]);

  return useQuery({
    ...getUsersQueryOptions({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
};

/**
 * React Query based custom hook for getting the data for all users with infinite scrolling.
 * Uses useInfiniteQuery to handle loading more data as the user scrolls.
 *
 * @param {Object} queryParams - The query parameters for fetching users.
 * @param {Object} options - Additional options for the useInfiniteQuery hook.
 * @returns {UseInfiniteQueryResult} The result of the useInfiniteQuery hook.
 */
export const useQueryUsersInfinite = (
  queryParams: Partial<UsersQueryParams>,
  options: InfiniteQueryOptionsType<any, Error>,
) => {
  const qp = {
    ...defaultQueryParams,
    ...queryParams,
  };

  const infiniteQueryOptions = {
    queryFn: async ({ pageParam }) =>
      fetchUsers({
        ...qp,
        offset: pageParam,
      }),
    queryKey: queryKeysUser.getAllInfinite(queryParams),
  };

  return createInfiniteQuery<any>(qp, infiniteQueryOptions);
};
